import { useEffect, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import {
  dequeueForLaunch,
  displayName,
  formatEta,
  fullName,
  isNotQueuedError,
  parkEntry,
  partyMarker,
  requeueStarted,
  undoConfirmMessage,
  unparkEntry,
  withinUndoWindow,
} from '../services/queue';
import { DEV_TOOLS_ENABLED } from '../utils/env';
import LanguageFlag from './LanguageFlag';
import QueuePickerModal from './QueuePickerModal';
import QueueManagePanel from './QueueManagePanel';

// Custom event PCTile dispatches when RealityAgent's `last_level_warning`
// notification arrives. Carries the active PC + the next-up customer's
// public display name ("Dirk v.") so the widget can show a "Volgende: …"
// banner. We also broadcast the same signal across browser tabs via
// BroadcastChannel so a separate /queue-display tab (no Electron, no IPC)
// picks it up. Never a full surname: the second screen is customer-facing.
export interface NextUpSignal {
  displayName: string;
  pcName: string;
}
export const NEXT_UP_EVENT = 'reality-operator:next-up';
export const NEXT_UP_CHANNEL = 'reality-operator:next-up';

declare global {
  interface WindowEventMap {
    [NEXT_UP_EVENT]: CustomEvent<NextUpSignal>;
  }
}

/**
 * Fire the "Volgende: …" notification on every surface that listens:
 *   - This tab/window via a DOM CustomEvent (NextUpWidget banner)
 *   - Other tabs on the same origin via BroadcastChannel (web QueueDisplay)
 *   - The Electron secondary BrowserWindow via IPC (Electron QueueDisplay)
 */
export function broadcastNextUp(signal: NextUpSignal): void {
  window.dispatchEvent(new CustomEvent(NEXT_UP_EVENT, { detail: signal }));
  try {
    const ch = new BroadcastChannel(NEXT_UP_CHANNEL);
    ch.postMessage(signal);
    ch.close();
  } catch {
    // BroadcastChannel unsupported (very old browsers) — best effort only.
  }
  window.electron?.queueDisplay?.notifyNextUp?.(signal);
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operatorIdFromSession(): string | null {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : null;
  } catch {
    return null;
  }
}

export default function NextUpWidget() {
  const queue = useQueueDequeue();
  const {
    entries,
    selectedEntry,
    selectedDequeueEntryId,
    dequeueOnLaunch,
    setDequeueOnLaunch,
    setSelected,
    loading,
  } = queue;
  const dispatch = useQueueDispatchOptional();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [nextUp, setNextUp] = useState<NextUpSignal | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Undo stays reachable for the RPC's 5-minute window after our own
  // dispatch, OR after any recent dequeue at the location (a colleague's
  // start, a started row after the queue emptied). A ticker retires the link
  // without needing a re-render trigger.
  const ownFresh =
    dispatch?.lastDispatch && withinUndoWindow(dispatch.lastDispatch.at)
      ? dispatch.lastDispatch
      : null;
  const latestFresh =
    queue.latestDequeue && withinUndoWindow(queue.latestDequeue.last_event_at)
      ? queue.latestDequeue
      : null;
  const undoable = !!dispatch && (!!ownFresh || !!latestFresh);
  const [, setUndoTick] = useState(0);
  useEffect(() => {
    if (!undoable) return;
    const t = setInterval(() => setUndoTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [undoable]);

  const pendingMove = dispatch?.pendingMove ?? null;

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    setNote(null);
    try {
      await fn();
      queue.refresh();
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function undoLast() {
    if (!dispatch) return;
    setBusy(true);
    setNote(null);
    try {
      let outcome = await dispatch.undoLast();
      if (typeof outcome === 'object') {
        if (!window.confirm(undoConfirmMessage(outcome.predicted))) {
          return;
        }
        outcome = await dispatch.undoLast({ force: true });
        if (typeof outcome === 'object') return;
      }
      if (outcome === 'undone_with_stop') {
        setNote('Teruggezet in de wachtrij en de ervaring is gestopt.');
      } else if (outcome === 'undone') {
        setNote('Laatste wachtrij-actie teruggedraaid.');
      } else {
        setNote('Geen recente actie om ongedaan te maken (binnen 5 minuten).');
      }
    } catch (err) {
      setNote(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function togglePark(entry: { queue_entry_id: string; parked: boolean }) {
    const operatorId = operatorIdFromSession();
    if (!operatorId) {
      setNote('Geen operator in sessie. Log eerst in.');
      return;
    }
    await withBusy(async () => {
      if (entry.parked) await unparkEntry(entry.queue_entry_id, operatorId);
      else await parkEntry(entry.queue_entry_id, operatorId);
    });
  }

  async function requeuePending() {
    if (!dispatch || !pendingMove) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    const pm = pendingMove;
    await withBusy(async () => {
      await requeueStarted(pm.entry.queue_entry_id, operatorId);
      dispatch.setPendingMove(null);
      setNote(`${fullName(pm.entry)} staat weer in de wachtrij.`);
    });
  }

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    function onSignal(e: CustomEvent<NextUpSignal>) {
      setNextUp(e.detail);
      // Auto-dismiss after 10s so the banner doesn't get stale; operator
      // can also click the ✕ to close earlier.
      if (t) clearTimeout(t);
      t = setTimeout(() => setNextUp(null), 10_000);
    }
    window.addEventListener(NEXT_UP_EVENT, onSignal);
    return () => {
      window.removeEventListener(NEXT_UP_EVENT, onSignal);
      if (t) clearTimeout(t);
    };
  }, []);

  if (loading && entries.length === 0) {
    return <div className="next-up-widget next-up-widget--loading">Wachtrij laden…</div>;
  }

  const e = selectedEntry;

  // Dev/staging only: same path the real PCTile launch hook takes, just without
  // touching a PC agent. Pops the given entry as reason='launched' (no PC) and
  // resets the override. Takes the entry explicitly so the closure carries
  // the narrowed non-null value past the async boundary.
  async function testStart(entry: typeof e) {
    if (!entry) return;
    setTestError(null);
    try {
      const operatorId = operatorIdFromSession();
      if (!operatorId) {
        setTestError('Geen operator in sessie. Log eerst in.');
        return;
      }
      await dequeueForLaunch(entry.queue_entry_id, operatorId, null);
      setSelected(null);
    } catch (err) {
      if (isNotQueuedError(err)) return;        // already popped — fine
      setTestError(err instanceof Error ? err.message : String(err));
    }
  }

  const undoLabel = ownFresh
    ? `↩ Ongedaan: ${ownFresh.pcName}`
    : latestFresh
      ? `↩ Ongedaan: ${fullName(latestFresh)}${latestFresh.started_pc_name ? ` (${latestFresh.started_pc_name})` : ''}`
      : '';
  const undoTitle = ownFresh
    ? `Zet ${ownFresh.customerName} terug in de wachtrij en stop de ervaring op ${ownFresh.pcName}.`
    : 'Laatste start/verwijdering op deze locatie terugdraaien (vraagt om bevestiging als het niet jouw actie was).';

  // Action links shared by the empty and the filled state, so undo / beheer
  // never disappear when the queue runs dry.
  const actionLinks = (
    <>
      {dispatch ? (
        <button
          type="button"
          className="next-up-edit-link next-up-dispatch-link"
          title="Open het 'Volgende bezoeker' venster: kies een vrije PC en start de geboekte ervaring."
          onClick={() => dispatch.openDispatch()}
          disabled={!e}
        >
          ▶ Starten
        </button>
      ) : null}
      {e ? (
        <>
          <button
            type="button"
            className="next-up-edit-link"
            onClick={() => setPickerOpen(true)}
          >
            Bewerken
          </button>
          <button
            type="button"
            className="next-up-edit-link"
            disabled={busy}
            title={e.parked
              ? 'Bezoeker is terug: weer aan de beurt op de eigen plek.'
              : 'Bezoeker is even weg (toilet, kleedkamer): houdt de plek, wordt overgeslagen tot Terug.'}
            onClick={() => void togglePark(e)}
          >
            {e.parked ? '↺ Terug' : '⏸ Even weg'}
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="next-up-edit-link"
        title="Wachtrij beheren: volgorde, verwijderen, toevoegen, ongedaan maken."
        onClick={() => setManageOpen(true)}
      >
        Beheer
      </button>
      {undoable ? (
        <button
          type="button"
          className="next-up-edit-link next-up-undo-link"
          disabled={busy}
          title={undoTitle}
          onClick={() => void undoLast()}
        >
          {busy ? 'Bezig…' : undoLabel}
        </button>
      ) : null}
      {DEV_TOOLS_ENABLED && e ? (
        <>
          <button
            type="button"
            className="next-up-edit-link"
            title="Simuleer de 'Bezoeker bijna klaar' notificatie zonder een echte PC agent."
            onClick={() =>
              broadcastNextUp({
                displayName: displayName(e),
                pcName: '[test]',
              })
            }
          >
            🔔 Test Volgende
          </button>
          <button
            type="button"
            className="next-up-edit-link"
            title="Simuleer een gestarte experience: verwijdert de geselecteerde klant uit de wachtrij (zelfde pad als een echte launch, zonder PC)."
            onClick={() => void testStart(e)}
          >
            🚀 Test Start
          </button>
        </>
      ) : null}
    </>
  );

  const banners = (
    <>
      {pendingMove && dispatch ? (
        <div className="next-up-banner next-up-banner--move" role="status">
          <span className="next-up-banner-label">Verplaatsen:</span>
          <span className="next-up-banner-action">
            <strong>{fullName(pendingMove.entry)}</strong> gestopt op {pendingMove.fromPcName}.
            {' '}Kies een andere PC of zet de bezoeker terug in de wachtrij.
          </span>
          <button
            type="button"
            className="queue-picker-secondary next-up-banner-btn"
            disabled={busy}
            onClick={() => dispatch.openDispatch(null, { pinned: pendingMove })}
          >
            Verplaatsen
          </button>
          <button
            type="button"
            className="queue-picker-secondary next-up-banner-btn"
            disabled={busy}
            onClick={() => void requeuePending()}
          >
            Terug in wachtrij
          </button>
        </div>
      ) : null}

      {testError ? (
        <div className="next-up-banner" role="status" onClick={() => setTestError(null)}>
          <span className="next-up-banner-label">Test:</span>
          <span className="next-up-banner-action">{testError}</span>
        </div>
      ) : null}

      {note ? (
        <div className="next-up-banner" role="status" onClick={() => setNote(null)}>
          <span className="next-up-banner-label">Wachtrij:</span>
          <span className="next-up-banner-action">{note}</span>
        </div>
      ) : null}

      {nextUp ? (
        <div className="next-up-banner" role="status">
          <span className="next-up-banner-label">Bezoeker bijna klaar op {nextUp.pcName}.</span>
          <span className="next-up-banner-action">
            Volgende: <strong>{nextUp.displayName}</strong>, naar voren halen.
          </span>
          <button
            type="button"
            className="next-up-banner-close"
            onClick={() => setNextUp(null)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>
      ) : null}

      {pickerOpen ? <QueuePickerModal onClose={() => setPickerOpen(false)} /> : null}
      {manageOpen ? <QueueManagePanel onClose={() => setManageOpen(false)} /> : null}
    </>
  );

  if (!e) {
    return (
      <>
        <div className="next-up-widget next-up-widget--empty">
          <div className="next-up-main">
            <div className="next-up-headline">
              <span className="next-up-empty-label">
                {entries.length === 0 ? 'Wachtrij is leeg' : 'Geen geselecteerde klant'}
              </span>
            </div>
            <div className="next-up-sub">{actionLinks}</div>
          </div>
        </div>
        {banners}
      </>
    );
  }

  const marker = partyMarker(e);
  const eta = e.eta_minutes !== null && e.eta_minutes > 0 ? formatEta(e.eta_minutes) : '';

  return (
    <>
      <div className={`next-up-widget ${e.parked ? 'next-up-widget--parked' : ''}`}>
        <div className="next-up-position">{e.position}</div>

        <div className="next-up-main">
          <div className="next-up-headline">
            <strong>{fullName(e)}</strong>
            <LanguageFlag code={e.play_language} className="next-up-flag" />
            {marker ? (
              <>
                <span className="next-up-sep">·</span>
                <span>{marker}</span>
              </>
            ) : null}
            <span className="next-up-sep">·</span>
            <span>{e.experience_names ?? '—'}</span>
            <span className="next-up-sep">·</span>
            <span>{formatTime(e.departure_start)}</span>
            {eta ? (
              <>
                <span className="next-up-sep">·</span>
                <span className="next-up-eta">{eta}</span>
              </>
            ) : null}
            {e.room_name ? (
              <>
                <span className="next-up-sep">·</span>
                <span className="next-up-room">{e.room_name}</span>
              </>
            ) : null}
            {e.parked ? <span className="queue-parked-pill">even weg</span> : null}
            {selectedDequeueEntryId ? (
              <span className="next-up-override-pill">handmatig geselecteerd</span>
            ) : null}
          </div>
          <div className="next-up-sub">
            {e.departure_number}
            {actionLinks}
          </div>
        </div>

        <label className="next-up-checkbox" title="Verwijder deze klant uit de wachtrij als je een experience start.">
          <input
            type="checkbox"
            checked={dequeueOnLaunch}
            onChange={(ev) => setDequeueOnLaunch(ev.target.checked)}
          />
          <span>Verwijder uit wachtrij bij start</span>
        </label>
      </div>

      {banners}
    </>
  );
}
